/**
 * useAdminAlert Hook
 * 
 * Hook สำหรับจัดการ admin alerts พร้อม auto-refresh และ sound
 * ⚠️ แยกจาก Member Notification (Notification class ใน Parse)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/components/auth'
import { AdminAlertService } from '@/domain/admin-alert/admin-alert.service'
import type { AdminAlert, AdminAlertCounts } from '@/domain/admin-alert/admin-alert.types'

interface UseAdminAlertOptions {
  enabled?: boolean
  interval?: number // milliseconds (default: 30000 = 30 seconds)
  soundEnabled?: boolean
  onNewAlert?: (counts: AdminAlertCounts) => void
}

export function useAdminAlert(options: UseAdminAlertOptions = {}) {
  const {
    enabled = true,
    interval = 30000, // 30 seconds
    soundEnabled: initialSoundEnabled = true,
    onNewAlert,
  } = options

  const { sessionToken } = useAuth()
  const [alerts, setAlerts] = useState<AdminAlert[]>([])
  const [counts, setCounts] = useState<AdminAlertCounts>({
    pendingDeposits: 0,
    pendingWithdraws: 0,
    newMembers: 0,
    total: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(initialSoundEnabled)
  const [error, setError] = useState<string | null>(null)

  const previousCountsRef = useRef<AdminAlertCounts>({
    pendingDeposits: 0,
    pendingWithdraws: 0,
    newMembers: 0,
    total: 0,
  })
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioInitializedRef = useRef(false)
  const soundRepeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastAlertTimeRef = useRef<number>(0)

  // Initialize audio context
  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
      if (AudioContextClass) {
        audioContextRef.current = new AudioContextClass()
        console.log(`🔊 [useAdminAlert] Audio context created: ${audioContextRef.current.state}`)
        
        if (audioContextRef.current.state === 'suspended') {
          console.log('⏸️ [useAdminAlert] Audio context suspended, waiting for user interaction...')
          const resumeAudio = () => {
            if (audioContextRef.current?.state === 'suspended') {
              audioContextRef.current.resume().then(() => {
                console.log('✅ [useAdminAlert] Audio context resumed via user interaction')
                audioInitializedRef.current = true
              }).catch(err => {
                console.warn('⚠️ [useAdminAlert] Could not resume audio:', err)
              })
            }
          }
          document.addEventListener('click', resumeAudio)
          document.addEventListener('touchstart', resumeAudio)
          document.addEventListener('keydown', resumeAudio)
          
          return () => {
            document.removeEventListener('click', resumeAudio)
            document.removeEventListener('touchstart', resumeAudio)
            document.removeEventListener('keydown', resumeAudio)
          }
        } else {
          console.log('✅ [useAdminAlert] Audio context ready immediately')
          audioInitializedRef.current = true
        }
      } else {
        console.warn('⚠️ [useAdminAlert] Web Audio API not supported')
      }
    } catch (error) {
      console.warn('⚠️ [useAdminAlert] Could not initialize audio context:', error)
    }
  }, [])

  // Play notification sound
  const playSound = useCallback(() => {
    if (!soundEnabled) {
      console.log('🔇 [useAdminAlert] Sound disabled - skipping')
      return
    }

    console.log('🔊 [useAdminAlert] Attempting to play sound...')

    try {
      const audioContext = audioContextRef.current
      if (audioContext) {
        const play = () => {
          if (audioContext.state === 'suspended') {
            console.log('⏸️ [useAdminAlert] Audio context suspended, attempting to resume...')
            audioContext.resume().then(() => {
              console.log('✅ [useAdminAlert] Audio context resumed, playing sound')
              playSoundWithWebAudio(audioContext)
            }).catch(err => {
              console.warn('⚠️ [useAdminAlert] Could not resume audio context, trying fallback:', err)
              playSoundFallback()
            })
          } else {
            console.log(`✅ [useAdminAlert] Audio context ready (${audioContext.state}), playing sound`)
            playSoundWithWebAudio(audioContext)
          }
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            console.log('✅ [useAdminAlert] Audio context resumed before playing')
            play()
          }).catch(err => {
            console.warn('⚠️ [useAdminAlert] Could not resume, using fallback:', err)
            playSoundFallback()
          })
        } else {
          play()
        }
        return
      }
    } catch (error) {
      console.warn('⚠️ [useAdminAlert] Web Audio API failed, trying fallback:', error)
    }

    playSoundFallback()
  }, [soundEnabled])

  // Play sound with Web Audio API
  const playSoundWithWebAudio = (audioContext: AudioContext) => {
    try {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime)
      oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.1)
      oscillator.type = 'sine'

      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.9, audioContext.currentTime + 0.05)
      gainNode.gain.linearRampToValueAtTime(0.7, audioContext.currentTime + 0.1)
      gainNode.gain.linearRampToValueAtTime(0.9, audioContext.currentTime + 0.15)
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.2)

      console.log('🔊 [useAdminAlert] Sound played (Web Audio API)')
    } catch (error) {
      console.warn('⚠️ [useAdminAlert] Error in playSoundWithWebAudio:', error)
      playSoundFallback()
    }
  }

  // Fallback: ใช้ HTML5 Audio
  const playSoundFallback = () => {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
      if (AudioContextClass) {
        const ctx = new AudioContextClass()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()

        osc.connect(gain)
        gain.connect(ctx.destination)

        osc.frequency.value = 1000
        osc.type = 'sine'

        gain.gain.setValueAtTime(0, ctx.currentTime)
        gain.gain.linearRampToValueAtTime(0.9, ctx.currentTime + 0.05)
        gain.gain.linearRampToValueAtTime(0.7, ctx.currentTime + 0.1)
        gain.gain.linearRampToValueAtTime(0.9, ctx.currentTime + 0.15)
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2)

        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.2)

        osc.onended = () => {
          ctx.close()
        }

        console.log('🔊 [useAdminAlert] Sound played (Fallback)')
      }
    } catch (error) {
      console.warn('⚠️ [useAdminAlert] Fallback sound failed:', error)
    }
  }

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (!sessionToken || !enabled) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await AdminAlertService.getAlerts(sessionToken, {
        limit: 20,
        includeNewMembers: true,
        newMembersHours: 24,
      })

      console.log("🔔 [useAdminAlert] Setting alerts:", {
        alertsCount: data.alerts.length,
        alerts: data.alerts.map((a) => ({ id: a.id, type: a.type, title: a.title })),
        counts: data.counts,
      })

      setAlerts(data.alerts)
      
      const previousTotal = previousCountsRef.current.total
      const newTotal = data.counts.total
      
      console.log(`📊 [useAdminAlert] Counts comparison:`, {
        previous: previousTotal,
        new: newTotal,
        hasPrevious: previousTotal > 0,
        isNew: newTotal > previousTotal,
        alertsCount: data.alerts.length,
        counts: data.counts,
      })
      
      // Debug: ถ้ามี counts แต่ไม่มี alerts
      if (newTotal > 0 && data.alerts.length === 0) {
        console.warn("⚠️ [useAdminAlert] Count > 0 but no alerts!", {
          counts: data.counts,
          alerts: data.alerts,
        })
      }

      if (newTotal > previousTotal) {
        if (previousTotal > 0) {
          console.log(`🔔 [useAdminAlert] New alert detected: ${previousTotal} → ${newTotal}`)
          console.log('🔊 [useAdminAlert] Triggering sound notification...')
          lastAlertTimeRef.current = Date.now()
          setTimeout(() => {
            playSound()
          }, 100)
          if (onNewAlert) {
            onNewAlert(data.counts)
          }
        } else {
          console.log(`📋 [useAdminAlert] Initial alerts loaded: ${newTotal} alerts`)
          if (newTotal > 0) {
            lastAlertTimeRef.current = Date.now()
          }
        }
      } else if (newTotal < previousTotal) {
        console.log(`📉 [useAdminAlert] Alerts decreased: ${previousTotal} → ${newTotal}`)
        if (newTotal === 0) {
          lastAlertTimeRef.current = 0
        }
      }

      previousCountsRef.current = data.counts
      setCounts(data.counts)
    } catch (err: any) {
      console.error('❌ [useAdminAlert] Error:', err)
      setError(err.message || 'ไม่สามารถโหลดข้อมูลแจ้งเตือนได้')
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, enabled, playSound, onNewAlert])

  // Fetch counts only (faster)
  const fetchCounts = useCallback(async () => {
    if (!sessionToken || !enabled) return

    try {
      const counts = await AdminAlertService.getCounts(sessionToken)
      
      const previousTotal = previousCountsRef.current.total
      const newTotal = counts.total
      
      console.log(`📊 [useAdminAlert] Auto-refresh counts:`, {
        previous: previousTotal,
        new: newTotal,
        hasPrevious: previousTotal > 0,
        isNew: newTotal > previousTotal,
      })

      if (newTotal > previousTotal || newTotal < previousTotal) {
        console.log(`🔄 [useAdminAlert] Count changed (${previousTotal} → ${newTotal}), fetching full alerts...`)
        
        const data = await AdminAlertService.getAlerts(sessionToken, {
          limit: 20,
          includeNewMembers: true,
          newMembersHours: 24,
        })
        
        setAlerts(data.alerts)
        setCounts(data.counts)
        
        if (newTotal > previousTotal) {
          if (previousTotal > 0) {
            console.log(`🔔 [useAdminAlert] New alert detected (auto-refresh): ${previousTotal} → ${newTotal}`)
            console.log('🔊 [useAdminAlert] Triggering sound notification (auto-refresh)...')
            lastAlertTimeRef.current = Date.now()
            setTimeout(() => {
              console.log('🔊 [useAdminAlert] Executing playSound() for new alert...')
              playSound()
            }, 50)
            if (onNewAlert) {
              onNewAlert(data.counts)
            }
          } else {
            console.log(`📋 [useAdminAlert] Initial alerts loaded (auto-refresh): ${newTotal} alerts`)
            lastAlertTimeRef.current = Date.now()
          }
        } else if (newTotal < previousTotal) {
          console.log(`📉 [useAdminAlert] Alerts decreased (auto-refresh): ${previousTotal} → ${newTotal}`)
          if (newTotal === 0) {
            lastAlertTimeRef.current = 0
          }
        }
        
        previousCountsRef.current = data.counts
      } else {
        console.log(`➡️ [useAdminAlert] Alert count unchanged (auto-refresh): ${newTotal}`)
        previousCountsRef.current = counts
        setCounts(counts)
      }
    } catch (err: any) {
      console.error('❌ [useAdminAlert] Error fetching counts:', err)
    }
  }, [sessionToken, enabled, playSound, onNewAlert])

  // Initial fetch
  useEffect(() => {
    if (enabled && sessionToken) {
      console.log('🔄 [useAdminAlert] Initial fetch triggered')
      fetchAlerts()
    } else {
      console.log('⏸️ [useAdminAlert] Not enabled or no session token:', { enabled, hasToken: !!sessionToken })
    }
  }, [enabled, sessionToken])

  // Auto-refresh
  useEffect(() => {
    if (!enabled || !sessionToken) {
      console.log('⏸️ [useAdminAlert] Auto-refresh disabled:', { enabled, hasToken: !!sessionToken })
      return
    }

    console.log(`🔄 [useAdminAlert] Auto-refresh enabled (every ${interval}ms)`)

    const intervalId = setInterval(() => {
      console.log('🔄 [useAdminAlert] Auto-refresh triggered')
      fetchCounts()
    }, interval)

    return () => {
      console.log('🛑 [useAdminAlert] Auto-refresh stopped')
      clearInterval(intervalId)
    }
  }, [enabled, sessionToken, interval, fetchCounts])

  // Manual refresh
  const refresh = useCallback(() => {
    fetchAlerts()
  }, [fetchAlerts])

  // Test sound function
  const testSound = useCallback(() => {
    console.log('🧪 [useAdminAlert] Testing sound...')
    playSound()
  }, [playSound])

  // Repeat sound notification
  useEffect(() => {
    if (counts.total > 0 && soundEnabled) {
      const repeatInterval = 60000 // 60 seconds
      
      const timeSinceLastAlert = Date.now() - lastAlertTimeRef.current
      const shouldRepeat = lastAlertTimeRef.current > 0 && timeSinceLastAlert < 5 * 60 * 1000
      
      if (shouldRepeat && !soundRepeatIntervalRef.current) {
        console.log(`🔔 [useAdminAlert] Starting repeat sound (every ${repeatInterval}ms) for ${counts.total} alerts`)
        
        setTimeout(() => {
          console.log('🔔 [useAdminAlert] First repeat sound')
          playSound()
        }, 2000)
        
        soundRepeatIntervalRef.current = setInterval(() => {
          console.log(`🔔 [useAdminAlert] Repeating sound notification (${counts.total} pending)`)
          playSound()
        }, repeatInterval)
      }
    } else {
      if (soundRepeatIntervalRef.current) {
        console.log('🛑 [useAdminAlert] Stopping repeat sound')
        clearInterval(soundRepeatIntervalRef.current)
        soundRepeatIntervalRef.current = null
      }
    }

    return () => {
      if (soundRepeatIntervalRef.current) {
        clearInterval(soundRepeatIntervalRef.current)
        soundRepeatIntervalRef.current = null
      }
    }
  }, [counts.total, soundEnabled, playSound])

  return {
    alerts,
    counts,
    isLoading,
    error,
    soundEnabled,
    setSoundEnabled,
    refresh,
    testSound,
  }
}

